/**
 * @fileoverview Navbar.jsx
 * 
 * © 2026 KITH Travels (Pvt) Ltd. All rights reserved.
 * This software is the confidential and proprietary information of KITH Travels.
 * You shall not disclose such Confidential Information and shall use it only in 
 * accordance with the terms of the license agreement you entered into with KITH Travels.
 */
import React, { useEffect, useState, useRef } from 'react'
import { IMG, NavItems } from '../../assets/Assets'
import { Link, NavLink, useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import LanguageWarning from '../ui/LanguageWarning'

function Navbar() {
    const [isScrolled, setIsScrolled] = useState(false);
    const [isHidden, setIsHidden] = useState(false);
    const [menuOpen, setMenuOpen] = useState(false);
    const [langDropdownOpen, setLangDropdownOpen] = useState(false);
    const lastScrollY = useRef(0);
    const location = useLocation();
    const { t, i18n } = useTranslation();

    const isDarkHeroPage = location.pathname === '/home' || location.pathname === '/';

    useEffect(() => {
        const handleScroll = () => {
            const currentScrollY = window.scrollY;
            setIsScrolled(currentScrollY > 150);
            setLangDropdownOpen(false);

            if (currentScrollY > 500) {
                if (currentScrollY > lastScrollY.current) {
                    setIsHidden(true);
                } else {
                    setIsHidden(false);
                }
            } else {
                setIsHidden(false);
            }
            lastScrollY.current = currentScrollY;
        };
        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    // Close menu when route changes
    useEffect(() => {
        setMenuOpen(false);
    }, [location]);

    // Handle body scroll lock when menu is open
    useEffect(() => {
        if (menuOpen) {
            document.body.style.overflow = 'hidden';
            window.__lenis?.stop();
            setIsScrolled(false);
        } else {
            document.body.style.overflow = 'unset';
            window.__lenis?.start();
            setIsScrolled(window.scrollY > 150);
        }
        return () => {
            document.body.style.overflow = 'unset';
            window.__lenis?.start();
        };
    }, [menuOpen]);

    const getTextColor = () => {
        if (isScrolled) return 'text-gray-800';
        return isDarkHeroPage ? 'text-white' : 'text-gray-800';
    };

    const langs = [
        { code: 'de', name: 'Deutsch', flag: '🇩🇪' },
        { code: 'en', name: 'English', flag: '🇬🇧' },
        { code: 'es', name: 'Español', flag: '🇪🇸' },
        { code: 'fr', name: 'Français', flag: '🇫🇷' },
        { code: 'it', name: 'Italiano', flag: '🇮🇹' },
        { code: 'ru', name: 'Русский', flag: '🇷🇺' },
        { code: 'zh', name: '中文', flag: '🇨🇳' }
    ];
    const currentLang = langs.find(l => l.code === (i18n.resolvedLanguage?.split('-')[0])) || langs[0];

    return (
        <>
            <div className={`fixed top-0 left-0 w-full z-50 transition-all duration-1000 ${isHidden ? '-translate-y-[150%]' : 'translate-y-0'} ${isScrolled ? 'pt-0' : 'pt-0'}`}>
                <LanguageWarning />
                <div className={`mx-auto transition-all duration-500 flex items-center justify-between px-6 lg:px-10 py-3.5 md:py-5 border-none ${isScrolled ? 'w-[95%] lg:w-[90%] bg-white/50 backdrop-blur-xl shadow-sm rounded-full mt-2' : 'w-full bg-transparent'}`}>

                    {/* Logo */}
                    <Link to="/" className='shrink-0'>
                        <img
                            src={IMG.KITH_logo}
                            alt="KITH Logo"
                            width="185"
                            height="80"
                            className={`transition-all duration-300 ${isScrolled ? 'w-24' : 'w-32'}`}
                        />
                    </Link>

                    {/* Desktop Nav - Change breakpoint to lg (1024px) to avoid disorientation at 845px */}
                    <ul className='hidden lg:flex items-center gap-1 xl:gap-2'>
                        {NavItems.map((item, index) => (
                            <li key={index}>
                                <NavLink
                                    to={item.path}
                                    className={({ isActive }) => `
                                        px-4 py-2 text-sm xl:text-[15px] font-normal tracking-wide rounded-full transition-all duration-300
                                        ${isActive
                                            ? 'bg-KITH-primary text-white font-medium'
                                            : `${getTextColor()} hover:bg-gray-100/60`
                                        }
                                    `}
                                >
                                    {t('nav.' + item.name.toLowerCase().replace(' ', '_'))}
                                </NavLink>
                            </li>
                        ))}
                    </ul>

                    {/* Action Buttons & Mobile Toggle */}
                    <div className='flex items-center gap-2 lg:gap-4'>
                        {/* Language Switcher */}
                        <div className="relative">
                            <button 
                                onClick={() => setLangDropdownOpen(!langDropdownOpen)}
                                className={`flex items-center gap-1.5 px-3 py-2 rounded-full transition-all ${getTextColor()} hover:bg-gray-100/60 text-sm font-medium`}
                            >
                                <span className="text-base">{currentLang.flag}</span>
                                <span className="uppercase hidden sm:inline">{currentLang.code}</span>
                                <i className="fi fi-br-angle-down text-[10px] ml-0.5"></i>
                            </button>
                            <div className={`absolute top-full right-0 mt-2 bg-white rounded-xl shadow-xl border border-gray-100 py-2 min-w-[140px] z-50 transition-all duration-300 origin-top-right ${langDropdownOpen ? 'opacity-100 scale-100 translate-y-0 pointer-events-auto' : 'opacity-0 scale-95 -translate-y-2 pointer-events-none'}`}>
                                {(() => {
                                    const sortedLangs = [...langs].sort((a, b) => {
                                        if (a.code === currentLang.code) return -1;
                                        if (b.code === currentLang.code) return 1;
                                        return 0;
                                    });
                                    return sortedLangs.map(l => {
                                        const isSelected = l.code === currentLang.code;
                                        return (
                                            <button
                                                key={l.code}
                                                onClick={() => {
                                                    i18n.changeLanguage(l.code);
                                                    setLangDropdownOpen(false);
                                                }}
                                                className={`flex items-center w-full px-4 py-2.5 transition-colors text-left text-sm ${isSelected ? 'bg-KITH-primary/10 text-KITH-primary font-semibold' : 'text-gray-700 hover:bg-gray-50'}`}
                                            >
                                                <span className="mr-3 text-lg">{l.flag}</span>
                                                {l.name}
                                            </button>
                                        );
                                    });
                                })()}
                            </div>
                        </div>

                        <Link
                            to="/find-my-tour"
                            className={`hidden lg:block px-6 py-2.5 rounded-full font-medium text-sm transition-all duration-300 ${isScrolled ? 'bg-KITH-primary-dark text-white hover:bg-KITH-primary' : `${isDarkHeroPage ? 'bg-white text-KITH-primary-dark hover:bg-gray-50' : 'bg-KITH-primary-dark text-white hover:bg-KITH-primary'}`}`}
                        >
                            {t('nav.my_tour')}
                        </Link>

                        <button
                            onClick={() => setMenuOpen(!menuOpen)}
                            aria-label={menuOpen ? "Close navigation menu" : "Open navigation menu"}
                            className={`lg:hidden p-2.5 rounded-xl transition-all ${menuOpen ? 'text-red-500 bg-red-100' : (isScrolled || !isDarkHeroPage ? 'text-gray-800 bg-gray-100' : 'text-white bg-white/10')}`}
                        >
                            <i className={`fi ${menuOpen ? 'fi-br-cross' : 'fi-br-menu-burger'} text-xl flex items-center`}></i>
                        </button>
                    </div>
                </div>
            </div>

            {/* Mobile Menu Overlay */}
            <div className={`lg:hidden fixed inset-0 z-40 bg-white/95 backdrop-blur-xl transition-all duration-500 ease-in-out ${menuOpen ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-full pointer-events-none'}`}>
                <div className='flex flex-col h-full p-10 pt-32 gap-6 items-center text-center overflow-y-auto'>
                    {NavItems.map((item, index) => (
                        <NavLink
                            key={index}
                            to={item.path}
                            onClick={() => setMenuOpen(false)}
                            className={({ isActive }) => `text-lg font-normal tracking-wide transition-all py-1 w-full ${isActive ? 'text-KITH-primary font-medium' : 'text-gray-800'}`}
                        >
                            {t('nav.' + item.name.toLowerCase().replace(' ', '_'))}
                        </NavLink>
                    ))}
                    <Link
                        to="/find-my-tour"
                        onClick={() => setMenuOpen(false)}
                        className='mt-4 w-full max-w-xs py-4 bg-KITH-primary text-white font-medium rounded-2xl text-lg shadow-xl'
                    >
                        {t('nav.my_tour')}
                    </Link>

                    <div className='mt-auto pt-10 flex gap-6'>
                        {['facebook', 'instagram', 'twitter'].map((social, idx) => (
                            <a key={idx} href="#" aria-label={`Follow KITH Travels on ${social.charAt(0).toUpperCase() + social.slice(1)}`} className='w-12 h-12 rounded-full bg-gray-100 flex items-center justify-center text-gray-600'>
                                <i className={`fi fi-brands-${social} text-xl`}></i>
                            </a>
                        ))}
                    </div>
                </div>
            </div>
        </>
    )
}

export default Navbar